/**
 * useChangelly Composable
 *
 * Provides methods to interact with Changelly API via paymaster service
 */

import { ref } from 'vue';
import { apiConfig } from '../config/api';

export interface ChangellyCurrency {
  name: string;
  ticker: string;
  fullName: string;
  enabled: boolean;
  enabledFrom: boolean;
  enabledTo: boolean;
  protocol: string;
  blockchain: string;
}

export interface ExchangeAmountResult {
  amountFrom: string;
  amountTo: string;
  networkFee: string;
  transactionSpeedForecast: string;
  validUntil: string;
}

export interface CreateTransactionResult {
  id: string;
  status: string;
  payinAddress: string;
  payinExtraId: string | null;
  amountExpectedFrom: string;
  amountExpectedTo: string;
  amountFrom: string;
  amountTo: string;
  networkFee: string;
  validUntil: string;
  createdAt: string;
}

export interface TransactionStatus {
  id: string;
  status: string;
  payinAddress: string;
  payinExtraId: string | null;
  payinHash: string | null;
  payoutAddress: string;
  payoutExtraId: string | null;
  payoutHash: string | null;
  amountFrom: string;
  amountTo: string;
  networkFee: string;
  validUntil: string;
  createdAt: string;
}

/**
 * Fixed Rate API types
 */
export interface FixRateForAmountResult {
  id: string;             // rateId - valid for 60 seconds
  result: string;         // exchange rate
  amountFrom: string;     // exact payin amount
  amountTo: string;       // fixed payout amount
  networkFee: string;     // network commission
  min: string;            // minimum exchangeable amount
  max: string;            // maximum exchangeable amount
  expiredAt: number;      // Unix timestamp when rate expires
}

export interface CreateFixTransactionResult {
  id: string;
  type: string;           // Always "fixed"
  status: string;
  payinAddress: string;
  payinExtraId: string | null;
  payoutAddress: string;
  payoutExtraId: string | null;
  amountExpectedFrom: string;
  amountExpectedTo: string;
  networkFee: string;
  payTill: string;        // Payment deadline
  createdAt: string;
}

export function useChangelly() {
  const loading = ref(false);
  const error = ref<Error | null>(null);

  const paymasterUrl = apiConfig.paymasterUrl;

  /**
   * Get list of available currencies
   */
  async function getCurrencies(): Promise<{ success: boolean; currencies: ChangellyCurrency[]; error?: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/currencies`);

      if (!response.ok) {
        throw new Error(`Failed to get currencies: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get currencies');
      }

      return {
        success: true,
        currencies: data.currencies
      };
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      return {
        success: false,
        currencies: [],
        error: err instanceof Error ? err.message : 'Unknown error'
      };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get exchange amount estimation
   */
  async function getExchangeAmount(params: {
    from: string;
    to: string;
    amountFrom?: string;
    amountTo?: string;
  }): Promise<ExchangeAmountResult> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/exchange-amount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to get exchange amount: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get exchange amount');
      }

      return data.data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get minimum exchange amount
   */
  async function getMinAmount(from: string, to: string): Promise<string> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/min-amount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ from, to }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get min amount: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get min amount');
      }

      return data.minAmount;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create exchange transaction
   */
  async function createTransaction(params: {
    from: string;
    to: string;
    address: string;
    amountFrom: string;
    extraId?: string;
    refundAddress?: string;
    refundExtraId?: string;
  }): Promise<CreateTransactionResult> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/create-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to create transaction: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create transaction');
      }

      return data.transaction;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get transaction status
   */
  async function getTransactionStatus(id: string): Promise<TransactionStatus> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/status/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to get transaction status: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get transaction status');
      }

      return data.status;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Validate wallet address
   */
  async function validateAddress(currency: string, address: string): Promise<{ valid: boolean; message?: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/validate-address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currency, address }),
      });

      if (!response.ok) {
        throw new Error(`Failed to validate address: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to validate address');
      }

      return {
        valid: data.valid,
        message: data.message,
      };
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get exchange rates for multiple currency pairs (batch)
   * Returns a map of "from_to" -> rate info
   */
  async function getBatchExchangeRates(pairs: Array<{ from: string; to: string; amountFrom?: string }>): Promise<
    Map<
      string,
      {
        success: boolean;
        rate?: ExchangeAmountResult;
        error?: string;
      }
    >
  > {
    loading.value = true;
    error.value = null;

    try {
      // Execute all rate queries in parallel
      const promises = pairs.map(async (pair) => {
        const key = `${pair.from}_${pair.to}`;
        try {
          const rate = await getExchangeAmount({
            from: pair.from,
            to: pair.to,
            amountFrom: pair.amountFrom || '1',
          });
          return { key, success: true, rate };
        } catch (err) {
          return {
            key,
            success: false,
            error: err instanceof Error ? err.message : 'Unknown error',
          };
        }
      });

      const results = await Promise.allSettled(promises);

      // Build result map
      const rateMap = new Map<
        string,
        {
          success: boolean;
          rate?: ExchangeAmountResult;
          error?: string;
        }
      >();

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { key, success, rate, error } = result.value;
          rateMap.set(key, { success, rate, error });
        } else {
          // Promise rejected - shouldn't happen as we catch errors above
          console.error('Batch rate query rejected:', result.reason);
        }
      });

      return rateMap;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get fixed rate for amount (supports amountTo)
   * Rate is valid for 60 seconds
   */
  async function getFixRateForAmount(params: {
    from: string;
    to: string;
    amountFrom?: string;
    amountTo?: string;
  }): Promise<FixRateForAmountResult> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/fix-rate-for-amount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to get fix rate: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get fix rate');
      }

      // Changelly returns array, take first item
      const rateData = Array.isArray(data.data) ? data.data[0] : data.data;
      if (!rateData) {
        throw new Error('No rate data returned');
      }

      return rateData;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create fixed rate transaction
   * Requires rateId from getFixRateForAmount
   */
  async function createFixTransaction(params: {
    from: string;
    to: string;
    address: string;
    rateId: string;
    amountFrom?: string;
    amountTo?: string;
    refundAddress: string;
    extraId?: string;
    refundExtraId?: string;
  }): Promise<CreateFixTransactionResult> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${paymasterUrl}/api/changelly/create-fix-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to create fix transaction: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create fix transaction');
      }

      return data.transaction;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Unknown error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    getCurrencies,
    getExchangeAmount,
    getMinAmount,
    createTransaction,
    getTransactionStatus,
    validateAddress,
    getBatchExchangeRates,
    getFixRateForAmount,
    createFixTransaction,
  };
}
